import React, { useContext } from "react";
import {
  CommunicationsSheetData,
  CommunicationsSheetProps,
} from "./SheetContent";
import { CommunicationMemberCard } from "./CommunicationMemberCard";
import { Button } from "@pushpress/pushpress-ui";
import { ChevronRight, Sparkles } from "lucide-react";
import { AppContext } from "../../../context/app";
import { MemberCard } from "./MemberCard";
import {
  calculateLocalizedDaysUntilCelebration,
  formatDaysUntilCelebration,
  getAnniversaryYear,
} from "./dateHeadingHelpers";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { useLegacyNavigate } from "../../../hooks/useLegacyNavigate";
import { useNavigate } from "../../../hooks/useNavigate";
import { cn } from "../../../utils/css";
import { SheetEmptyState } from "./SheetEmptyState";
import { useDashboard } from "../DashboardProvider/DashboardProvider";
import { useExperiment } from "../../../hooks/useExperiment";
import { SHOW_MEMBER_PROFILE_FEATURE_FLAG } from "../../../context/features/features.utils";

export const CommunicationSheetContent: React.FC<CommunicationsSheetProps> = ({
  data,
  title,
  reportUrl,
}) => {
  const { useExperimentValue } = useExperiment();
  const isMemberProfilePageEnabled = useExperimentValue(
    SHOW_MEMBER_PROFILE_FEATURE_FLAG,
    true,
  ) as boolean;

  const { celebrations, celebrationsMetadata } =
    data as CommunicationsSheetData;
  const app = useContext(AppContext);
  const { client } = app;
  const { trackEvent } = useAnalytics();
  const legacyNavigate = useLegacyNavigate();
  const navigate = useNavigate();
  const { localCelebrationsMetadataCache } = useDashboard();
  const isClientUSOrCAN =
    client?.countryIso2 === "US" || client?.countryIso2 === "CA";
  const canSeeTextMessages =
    isClientUSOrCAN && app?.client?.bankConnected === 1;

  const showEmptyState = celebrations.length === 0;

  return (
    <div className="tw-flex tw-flex-col tw-h-full">
      <div
        id="sheet-container"
        className="tw-flex tw-flex-col tw-max-h-[80vh] sm:tw-max-h-screen tw-flex-1"
      >
        {/* Inner container */}
        <div
          id="sheet-inner-container"
          className="tw-p-4 tw-space-y-4 tw-flex-1 tw-overflow-auto"
        >
          {/* Banner */}
          <div id="sheet-banner">
            {canSeeTextMessages && celebrations.length > 0 && (
              <div
                className={cn(
                  "tw-bg-gradient-to-r tw-from-blue-50 tw-to-purple-50 tw-rounded-lg tw-p-3 tw-flex tw-items-center tw-justify-center tw-space-x-2",
                  "tw-border-2 tw-border-indigo-300",
                )}
              >
                <Sparkles size={18} className="tw-text-blue-600" />
                <span className="tw-text-gray-700 tw-text-sm tw-font-medium">
                  Use AI to generate text messages
                </span>
              </div>
            )}
          </div>

          {/* Empty State */}
          {showEmptyState && <SheetEmptyState type="communications" />}

          {/* Items */}
          <div id="sheet-items" className="tw-space-y-4 tw-flex-1 tw-pb-12">
            {!showEmptyState &&
              celebrations.map((celebration) => {
                const celebrationType =
                  celebration.type === "birthday" ? "Birthday" : "Anniversary";
                const daysUntilCelebration =
                  calculateLocalizedDaysUntilCelebration({
                    celebrationDate: celebration.celebrationDate,
                  });
                const formattedDaysUntilCelebration =
                  formatDaysUntilCelebration(
                    daysUntilCelebration,
                    celebrationType,
                    celebration.celebrationDate,
                  );
                const numberOfYears = getAnniversaryYear(
                  celebration.celebrationDate,
                );
                const showMemberCard =
                  !canSeeTextMessages || !celebration.phone;

                const hasReceivedSms = [
                  ...celebrationsMetadata,
                  ...localCelebrationsMetadataCache,
                ].some((metadata) => {
                  return (
                    metadata.userUuid === celebration.userUuid &&
                    metadata.eventType === celebration.type
                  );
                });

                if (showMemberCard) {
                  return (
                    <MemberCard
                      key={`${celebration.userUuid}-${celebration.type}`}
                      profilePic={celebration.userImageUrl}
                      firstName={celebration.firstName}
                      lastName={celebration.lastName}
                      cardSubheading={formattedDaysUntilCelebration}
                      onViewProfile={() => {
                        trackEvent("Member Profile Card Clicked", {
                          type: celebration.type,
                          card_type: "standard_member_card",
                        });
                        legacyNavigate(
                          isMemberProfilePageEnabled
                            ? `/user/${celebration.userUuid}`
                            : `/members/view/${celebration.userUuid}`,
                        );
                      }}
                    />
                  );
                }
                return (
                  <CommunicationMemberCard
                    key={`${celebration.userUuid}-${celebration.type}`}
                    recipientUuid={celebration.userUuid}
                    profilePic={celebration.userImageUrl}
                    firstName={celebration.firstName}
                    lastName={celebration.lastName}
                    celebrationSubheading={formattedDaysUntilCelebration}
                    type={celebration.type}
                    numberOfYears={numberOfYears}
                    phoneNumber={celebration.phone}
                    hasReceivedSms={hasReceivedSms}
                    onViewProfile={() => {
                      trackEvent("Member Profile Card Clicked", {
                        type: celebration.type,
                        card_type: "communication_member_card",
                      });
                      legacyNavigate(
                        isMemberProfilePageEnabled
                          ? `/user/${celebration.userUuid}`
                          : `/members/view/${celebration.userUuid}`,
                      );
                    }}
                  />
                );
              })}
          </div>
        </div>

        {/* Footer */}
        <div
          id="sheet-footer"
          className="tw-bg-white tw-p-4 tw-border-t tw-sticky tw-bottom-0"
        >
          {reportUrl && (
            <div className="tw-flex tw-justify-center tw-w-full">
              <Button
                variant="outline"
                onClick={() => navigate(reportUrl)}
                className="tw-w-full tw-shadow-none"
              >
                <div>View all {title?.toLowerCase()}</div>
                <div>
                  <ChevronRight className="tw-w-4 tw-h-4" />
                </div>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
